/*
 * Repainting a panel without throwing away where the user was.
 *
 * ── Why this is in shared-ui ──────────────────────────────────────────────
 * It lived in apps/retailer/src/pages/quote-builder/picker-repaint.ts and was
 * wired into the three pickers there. It is not picker code and never was:
 * it touches no Quote Builder concept, only a host element, its scrolled
 * ancestors and the focused field.
 *
 * Keeping it in one page's folder had a cost that showed up twice. The Quote
 * Builder's own right-hand panel had the identical bug for three days after
 * the pickers were fixed, because nobody looked in a picker file for it
 * (Steve, 2026-09-10: every line edit threw the quote back to the top). And
 * messages/page.ts hand-rolls its own narrower version of the same restore.
 * Around thirty files in the retailer app replace a host wholesale, so the
 * next one to need this should find it rather than write a fourth copy.
 *
 * ── The bug ───────────────────────────────────────────────────────────────
 * A panel renders by replacing its whole host: `host.innerHTML = ...`. That is
 * fine in itself — these are small, cheap renders — but the host usually sits
 * inside a single scroll container (`overflow: auto`). Emptying the host
 * collapses that container's content height to nothing, the browser clamps
 * its scrollTop to 0, and the rebuilt markup renders from the top.
 *
 * So choosing a range, a colour or a size on a multi-leaf unit threw the panel
 * back to the top — worst exactly where it hurts most, on a four-door larder
 * whose Frontal section is the reason you scrolled down in the first place.
 * Reported by Steve 2026-09-07 against the SJB picker; it was never
 * SJB-specific.
 *
 * ── Why not diff-render ───────────────────────────────────────────────────
 * Rendering only the section that changed is the deeper fix and is how the
 * colour combobox already works (it repaints just its `<ul>`, because a full
 * repaint destroyed the input being typed into and put the caret at 0). But
 * that is a rewrite across every panel. Preserving scroll and focus around
 * the existing full repaint fixes the reported symptom everywhere, in one
 * place, without touching a single render function.
 *
 * ── What jsdom can and cannot tell us ─────────────────────────────────────
 * jsdom STORES scrollTop but computes no layout, so it never clamps it: the
 * bug is invisible there and a test asserting "scroll survived a repaint"
 * PASSES WITH THE FIX REMOVED. That is the failure mode this codebase has hit
 * four times. So the harness instead drives `repaintPreservingScroll` with a
 * render callback that zeroes the scroller itself — modelling what the browser
 * does — which fails loudly if the restore is gone.
 */

/** Scroll offsets to put back after a repaint, innermost first. */
interface ScrollMemo {
  el: Element;
  top: number;
  left: number;
}

/**
 * Ancestors of `host` that are currently scrolled.
 *
 * Deliberately NOT chosen by computed style. jsdom resolves no stylesheets, so
 * an `overflow: auto` lookup is unanswerable there — and a non-zero scrollTop
 * is better evidence anyway: it means this element really is the one that
 * scrolls, in whatever the live layout turned out to be. An element scrolled
 * to 0 needs no restoring, so skipping it costs nothing.
 */
function scrolledAncestors(host: Element): ScrollMemo[] {
  const out: ScrollMemo[] = [];
  let el: Element | null = host.parentElement;
  while (el) {
    if (el.scrollTop > 0 || el.scrollLeft > 0) {
      out.push({ el, top: el.scrollTop, left: el.scrollLeft });
    }
    el = el.parentElement;
  }
  return out;
}

/** A stable-enough identity for the focused control, so focus can be handed
 *  back to the SAME field after its element has been replaced. `data-role` is
 *  how every picker already addresses its controls, and `data-leaf` separates
 *  one leaf's size dropdown from the next one's. */
function focusKey(el: Element): string | null {
  const role = el.getAttribute("data-role");
  if (!role) return null;
  const leaf = el.getAttribute("data-leaf");
  return leaf === null ? `[data-role='${role}']` : `[data-role='${role}'][data-leaf='${leaf}']`;
}

/**
 * Run `render` (which is expected to replace `host`'s contents wholesale) and
 * put back the scroll position and the focused field.
 *
 * FIRST PAINT IS DEFERRED TO, NOT OVERRIDDEN. When the host is empty going in,
 * this is a mount rather than a repaint, and index.ts deliberately sends the
 * pane to the top when a picker opens — a picker opening is a navigation. If
 * we restored then, we would fight that and land the retailer partway down a
 * freshly opened configurator at whatever offset the browse list had reached.
 */
export function repaintPreservingScroll(host: HTMLElement, render: () => void): void {
  const firstPaint = host.childElementCount === 0;
  if (firstPaint) { render(); return; }

  const memos = scrolledAncestors(host);
  const doc = host.ownerDocument;
  const active = doc.activeElement;
  const wasInside = !!active && active !== doc.body && host.contains(active);
  const key = wasInside && active ? focusKey(active) : null;
  // Only meaningful for text entry; a toggle button has no selection.
  const caret = wasInside && active && "selectionStart" in active
    ? (active as HTMLInputElement).selectionStart
    : null;

  render();

  // Restore unconditionally rather than only when it looks wrong: reading
  // scrollTop back to compare would be a second forced reflow for no benefit,
  // and assigning an unchanged value is a no-op in the browser.
  for (const m of memos) {
    m.el.scrollTop = m.top;
    m.el.scrollLeft = m.left;
  }

  if (!key) return;
  const next = host.querySelector<HTMLElement>(key);
  if (!next) return;
  next.focus();
  if (caret !== null && "setSelectionRange" in next) {
    // A replaced input focuses with the caret at 0, which is what put the
    // second typed character before the first in the colour filter. Same
    // cause, so the same guard.
    try { (next as HTMLInputElement).setSelectionRange(caret, caret); } catch { /* not a text input */ }
  }
}
